import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "../axiosConfig";

interface User {
  userId: string;
  email: string;
  primaryListingId: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string) => Promise<void>;
  signup: (email: string, name: string, primaryListingId?: string) => Promise<void>;
  verifyCode: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const response = await axios.get("/api/auth/me", {
        withCredentials: true,
      });
      setUser(response.data.user);
    } catch (error) {
      setUser(null);
    }
  };

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, []);

  const login = async (email: string) => {
    const response = await axios.post(
      "/api/auth/login",
      { email },
      { withCredentials: true }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to send login link");
    }
  };

  const signup = async (email: string, name: string, primaryListingId?: string) => {
    const response = await axios.post(
      "/api/auth/signup",
      { email, name, primaryListingId },
      { withCredentials: true }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || "Failed to create account");
    }
  };

  const verifyCode = async (code: string) => {
    const response = await axios.post(
      "/api/auth/verify",
      { code },
      { withCredentials: true }
    );
    if (response.data.success) {
      setUser(response.data.user);
    } else {
      throw new Error(response.data.error || "Failed to verify code");
    }
  };

  const logout = async () => {
    await axios.post("/api/auth/logout", {}, { withCredentials: true });
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, signup, verifyCode, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
